import { FilterOption, FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { getFilterValues } from '@/hooks/useTableFilters';

import { FILTER_KEY, FilterKey, MAX_FILTER_VALUES, TARGET_TYPE } from './constants';

export interface AuditFilterParams {
  sourceIds?: string[];
  brainIds?: string[];
  deskIds?: string[];
  targetIds?: string[];
  targetTypes?: string[];
}

/**
 * Maps the chip filter state to the backend `*_ids[]` params.
 *
 * - "All selected" for a filter → omit its param (= show everything), but only once that
 *   filter's options have loaded, so a transient partial list is never sent as the full set.
 * - Knowledge bases + Connections share the single `target_ids[]` param (and its 25-cap);
 *   `target_types` records which kinds are being filtered.
 */
export const deriveAuditParams = (
  filters: FilterItem[],
  optionsByFilter: Record<FilterKey, FilterOption[]>,
  loadedByFilter: Record<FilterKey, boolean>
): AuditFilterParams => {
  const resolve = (filter: FilterKey): string[] => {
    const selected = [...new Set(getFilterValues(filters, filter))];
    const optionValues = new Set(optionsByFilter[filter].map(({ value }) => value));
    const hasSelectedEveryOption =
      selected.length === optionValues.size && selected.every((value) => optionValues.has(value));
    if (loadedByFilter[filter] && optionValues.size > 0 && hasSelectedEveryOption) {
      return [];
    }
    return selected;
  };

  const sourceIds = resolve(FILTER_KEY.people);
  const brainIds = resolve(FILTER_KEY.brains);
  const deskIds = resolve(FILTER_KEY.desks);
  const collections = resolve(FILTER_KEY.collections);
  const connections = resolve(FILTER_KEY.connections);

  const targetIds = [...new Set([...collections, ...connections])].slice(0, MAX_FILTER_VALUES);
  // Types must describe the ids that survived the cap: the backend ANDs the two params, so a
  // type with no surviving ids can never match.
  const survived = new Set(targetIds);
  const targetTypes = [
    ...(collections.some((id) => survived.has(id)) ? [TARGET_TYPE[FILTER_KEY.collections]] : []),
    ...(connections.some((id) => survived.has(id)) ? [TARGET_TYPE[FILTER_KEY.connections]] : []),
  ];

  return {
    sourceIds: sourceIds.length ? sourceIds : undefined,
    brainIds: brainIds.length ? brainIds : undefined,
    deskIds: deskIds.length ? deskIds : undefined,
    targetIds: targetIds.length ? targetIds : undefined,
    targetTypes: targetTypes.length ? targetTypes : undefined,
  };
};
